import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, type DrawerProps, IconButton, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type ImmovableAssetFormType, getImmovableAssetFormSchema } from "Forms/Schemas/Assets/immovableAssetFormSchema";
import {
  getGetAssetsOverviewQueryKey,
  getListImmovableAssetItemsQueryKey,
  useCreateImmovableAssetItem,
  useDeleteAssetItem,
  useUpdateImmovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type ImmovableAssetItem, ImmovableAssetItemImmovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { IMMOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly immovableAsset?: ImmovableAssetItem;
} & DrawerProps;

export const ImmovableAssetDrawer: FC<Props> = ({ onClose, immovableAsset, open, ...rest }) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListImmovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateImmovableAssetItem();
  const { mutate: deleteItem } = useDeleteAssetItem();
  const { mutate: update } = useUpdateImmovableAssetItem();

  const immovableAssets: Option[] = (Object.keys(ImmovableAssetItemImmovableSubType) as Array<ImmovableAssetItemImmovableSubType>).map(
    (value) => ({
      value,
      name: t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType[IMMOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
    }),
  );

  const methods = useForm<ImmovableAssetFormType>({
    resolver: yupResolver(getImmovableAssetFormSchema(t)),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        immovableAssetType: immovableAsset?.immovableSubType,
        name: immovableAsset?.name,
        value: immovableAsset?.amount.value,
        isCoveredByInsurance: immovableAsset?.isCoveredByInsurance,
        isUnderFinancing: immovableAsset?.isUnderFinancing,
        isUsableForGoals: immovableAsset?.isUsableForGoals,
        note: immovableAsset?.note ?? undefined,
      };
      reset(defaultValues);
      return;
    }

    reset({});
  }, [immovableAsset, open, reset]);

  const onSubmit = (data: ImmovableAssetFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        immovableSubType: data.immovableAssetType as ImmovableAssetItemImmovableSubType,
        isCoveredByInsurance: !!data.isCoveredByInsurance,
        isUnderFinancing: !!data.isUnderFinancing,
        isUsableForGoals: !!data.isUsableForGoals,
      };

      if (!immovableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: immovableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: immovableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Deleted));
            invalidate();
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const immovableAssetName = watch("name");

  const onChangeType = (value: string) => {
    if (immovableAssetName && immovableAssetName.length > 0) return;
    const preFilledName = immovableAssets.find((ia) => ia.value === value)?.name;
    setValue("name", preFilledName ?? "");
  };

  return (
    <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
            <Typography variant="h2">
              {immovableAsset ? t(Resources.AnalysisDetail.Assets.Immovable.Edit) : t(Resources.AnalysisDetail.Assets.Immovable.Add)}
            </Typography>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DrawerContent>
            <Stack spacing={Spacing.XL} p={Spacing.XXL}>
              <FormDropdown
                name="immovableAssetType"
                options={immovableAssets}
                control={control}
                disabled={readOnly}
                labelEndAdornment={<StyledLabelAdornment />}
                label={t(Resources.AnalysisDetail.Assets.Immovable.ImmovableAssetType.Title)}
                errors={errors}
                onChange={onChangeType}
              />
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="name"
                label={t(Resources.AnalysisDetail.Assets.Name)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
              />
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="value"
                label={t(Resources.AnalysisDetail.Assets.Value)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
                unmask="typed"
                placeholder="0"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 0,
                    min: 0,
                  },
                ]}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    {t(Resources.Common.CZKSuffix)}
                  </Typography>
                }
              />
              <BlFormInput
                disabled={readOnly}
                control={control}
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                name="note"
                errors={errors}
                multiline
                minRows={6}
                maxRows={8}
                characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
              />
              <Divider />
              <Typography fontWeight={600} variant="subtitle1">
                {t(Resources.AnalysisDetail.Assets.Attributes)}
              </Typography>
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="isUsableForGoals"
                text={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                errors={errors}
              />
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="isCoveredByInsurance"
                text={t(Resources.AnalysisDetail.Assets.IsCoveredByInsurance)}
                errors={errors}
              />
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="isUnderFinancing"
                text={t(Resources.AnalysisDetail.Assets.IsUnderFinancing)}
                errors={errors}
              />
            </Stack>
          </DrawerContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
            <Box>
              {!!immovableAsset && (
                <Button
                  variant="text"
                  color="error"
                  disabled={readOnly}
                  sx={{
                    marginRight: Spacing.XL,
                  }}
                  onClick={onDelete}
                >
                  {t(Resources.Common.Delete)}
                </Button>
              )}
            </Box>
            <Box display="flex" flexDirection="row" justifyContent="flex-end">
              <Button
                onClick={onClose}
                color="secondary"
                sx={{
                  marginRight: Spacing.XL,
                }}
              >
                {t(Resources.Common.Close)}
              </Button>
              <Button disabled={readOnly} type="submit">
                {immovableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  );
};
